import React, { useCallback, useRef, useState } from 'react';

import type { EditEntries } from '@aline/common';
import { EditingMode } from '@aline/common';
import { Menu, MenuItem, Typography } from '@mui/material';
import { equals } from 'ramda';
import { AlineTooltip, SelectButton } from 'src/components';
import { SuggestionStatus } from 'src/components/ai/chat/chat-types';
import { CheckIcon } from 'src/components/icons/CheckIcon';
import { RejectChangeXIcon } from 'src/components/icons/RejectChangeXIcon';
import { SmallDownArrowIcon } from 'src/components/icons/SmallDownArrowIcon';
import { useEditorSuggestionState } from 'src/editor/editor-suggestion-state-registry';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { clearStaleSuggestionMarkers } from 'src/editor/EditorAlert/SuggestionsAlert/clear-stale-suggestion-marker';
import { useSyncSuggestionStatusToServer } from 'src/editor/EditorAlert/SuggestionsAlert/useSuggestionActions';
import { Queries } from 'src/editor/queries';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useSelection } from 'src/layout-engine/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  acceptButton: {
    borderRadius: '3px 0px 0px 3px',
    height: 32,
    marginLeft: theme.spacing(0.5),
  },
  acceptText: {
    color: 'inherit',

    marginLeft: theme.spacing(0.5),
    // Override the textTransform: 'uppercase' that is default on Material-UI buttons.
    textTransform: 'none',
  },
  arrow: {
    height: 12,
    width: 12,
  },
  arrowButton: {
    borderRadius: '0px 3px 3px 0px',
    height: 32,
  },
}));

type TrackedChangesAction = 'accept' | 'reject';
interface AcceptRejectMenuProps {
  action: TrackedChangesAction;
  disabled?: boolean;
}

// TODO: If changing, consider migrating to DeclarativeMenu
export function AcceptRejectMenu(props: AcceptRejectMenuProps): JSX.Element {
  const { action, disabled } = props;
  const editor = useEditor();
  const { classes } = useStyles();
  const {
    documentUi: { setEditingMode },
  } = useEditorTabStateActions();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const {
    acceptOrRejectAllChanges,
    acceptOrRejectSelectedChanges,
    acceptOrRejectSelectedChangesAndMoveToNext,
  } = useTrackedChangesAPI();
  const { selectedSuggestedEdits } = useGetSelectedSuggestedEdits();

  const isAcceptVariant = action === 'accept';
  const decisionTitle = isAcceptVariant ? 'Accept' : 'Reject';
  const decisionIcon = isAcceptVariant ? <CheckIcon /> : <RejectChangeXIcon />;

  /**
   * Note: We use Promise.resolve in event handlers below because, when clicking a menu item, it causes the focus to be stolen from AlineTransforms.focus in the
   * relevant click handler.  But moving the function execution to promise portion of the event loop restores the focus correctly to the editor.
   */
  return (
    <>
      <AlineTooltip arrow title={`${decisionTitle} and move to next`}>
        <div>
          <SelectButton
            ref={buttonRef}
            className={classes.acceptButton}
            disabled={disabled}
            selected={menuOpen}
            onClick={() => {
              acceptOrRejectSelectedChangesAndMoveToNext(action);
              ClientTransforms.focus(editor);
            }}
          >
            {decisionIcon}
            <Typography className={classes.acceptText} variant="caption">
              {decisionTitle}
            </Typography>
          </SelectButton>
        </div>
      </AlineTooltip>
      <SelectButton
        className={classes.arrowButton}
        disabled={disabled}
        selected={menuOpen}
        onClick={(): void => setMenuOpen(!menuOpen)}
      >
        <SmallDownArrowIcon className={classes.arrow} />
      </SelectButton>
      <Menu
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={menuOpen}
        onClose={(): void => setMenuOpen(false)}
      >
        <MenuItem
          disabled={!selectedSuggestedEdits.length}
          onClick={(): void => {
            setMenuOpen(false);
            acceptOrRejectSelectedChanges(action);
            ClientTransforms.focus(editor);
          }}
        >
          <Typography variant="body1">{`${decisionTitle} this change`}</Typography>
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            setMenuOpen(false);
            acceptOrRejectSelectedChangesAndMoveToNext(action);
            ClientTransforms.focus(editor);
          }}
        >
          <Typography variant="body1">{`${decisionTitle} and move to next`}</Typography>
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            setMenuOpen(false);
            Promise.resolve().then(() => {
              acceptOrRejectAllChanges(action);
              ClientTransforms.focus(editor);
            });
          }}
        >
          <Typography variant="body1">{`${decisionTitle} all changes`}</Typography>
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            setMenuOpen(false);
            setEditingMode(EditingMode.EDITING);
            Promise.resolve().then(() => {
              acceptOrRejectAllChanges(action);
              ClientTransforms.focus(editor);
            });
          }}
        >
          <Typography variant="body1">{`${decisionTitle} all changes and stop tracking`}</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}

export function useTrackedChangesAPI(): {
  acceptOrRejectAllChanges: (action: TrackedChangesAction) => void;
  acceptOrRejectSelectedChanges: (action: TrackedChangesAction) => void;
  acceptOrRejectSelectedChangesAndMoveToNext: (action: TrackedChangesAction) => void;
  keepSelectedChanges: () => void;
} {
  const editor = useEditor();
  const {
    derived: { setSuggestedEdits },
  } = useEditorTabStateActions();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { recomputeSuggestions } = useEditorSuggestionState();
  const { syncSuggestionStatusToServer } = useSyncSuggestionStatusToServer();

  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const rWHeightPx = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const rWOffsetPx = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);

  const acceptOrRejectSelectedChanges = useCallback(
    (action: TrackedChangesAction): EditEntries => {
      const allChanges = Queries.sortedEditRanges(editor);
      const selectedChanges = Queries.selectedEdits(editor, allChanges);
      if (!selectedChanges?.length || !editingMode || !currentUserId) return [];

      switch (action) {
        case 'accept': {
          const acceptedSources = AuthorizedTransforms.acceptSuggestedEdits(editor, {
            authorId: currentUserId,
            editEntries: selectedChanges,
            editingMode,
          });
          const acceptedSourcesToUpdate = acceptedSources.filter(source => {
            return !Queries.docHasSuggestionWithGroupId(editor, { groupId: source.groupId });
          });
          if (acceptedSourcesToUpdate.length) {
            // Intentionally do not await promise to avoid blocking UI...
            syncSuggestionStatusToServer(acceptedSourcesToUpdate, SuggestionStatus.Accepted);
          }
          break;
        }
        case 'reject': {
          const rejectedSources = AuthorizedTransforms.rejectSuggestedEdits(editor, {
            authorId: currentUserId,
            editEntries: selectedChanges,
            editingMode,
          });
          const rejectedSourcesToUpdate = rejectedSources.filter(source => {
            return !Queries.docHasSuggestionWithGroupId(editor, { groupId: source.groupId });
          });
          if (rejectedSourcesToUpdate.length) {
            // Intentionally do not await promise to avoid blocking UI...
            syncSuggestionStatusToServer(rejectedSourcesToUpdate, SuggestionStatus.Rejected);
          }
          break;
        }
      }

      // Must be after transform to get correct value.
      const newEditEntries = Queries.sortedEditRanges(editor);
      setSuggestedEdits(newEditEntries);

      return newEditEntries;
    },
    [
      AuthorizedTransforms,
      currentUserId,
      editingMode,
      editor,
      setSuggestedEdits,
      syncSuggestionStatusToServer,
    ]
  );
  /**
   * Keep ("redline") the sourced AI suggestions the selection touches,
   * scoped the same way as `acceptOrRejectSelectedChanges`. The edits stay
   * pending but detach from their suggestion group, so the markers and chat
   * status of fully-detached groups resolve to Redlined.
   */
  const keepSelectedChanges = useCallback((): void => {
    if (!editingMode || !currentUserId) return;

    const keptSources = AuthorizedTransforms.keepSelectedSuggestions(editor, {
      authorId: currentUserId,
      editingMode,
    });
    if (!keptSources.length) return;

    /**
     * Stripping a source only dirties the edited paths, so the marker pair of
     * a fully-detached group (possibly in other paragraphs) is not guaranteed
     * to renormalize away — remove stale markers explicitly.
     */
    clearStaleSuggestionMarkers(editor);

    const keptSourcesToUpdate = keptSources.filter(source => {
      return !Queries.docHasSuggestionWithGroupId(editor, { groupId: source.groupId });
    });
    if (keptSourcesToUpdate.length) {
      // Intentionally do not await promise to avoid blocking UI...
      syncSuggestionStatusToServer(keptSourcesToUpdate, SuggestionStatus.Redlined);
    }

    // Must be after transform to get correct value.
    setSuggestedEdits(Queries.sortedEditRanges(editor));
    recomputeSuggestions();
  }, [
    AuthorizedTransforms,
    currentUserId,
    editingMode,
    editor,
    recomputeSuggestions,
    setSuggestedEdits,
    syncSuggestionStatusToServer,
  ]);
  const acceptOrRejectSelectedChangesAndMoveToNext = useCallback(
    (action: TrackedChangesAction): void => {
      const newEditEntries = acceptOrRejectSelectedChanges(action);
      ClientTransforms.scrollToNextChange(editor, {
        editEntries: newEditEntries,
        rWHeightPx,
        rWOffsetPx,
      });
    },
    [acceptOrRejectSelectedChanges, editor, rWHeightPx, rWOffsetPx]
  );
  const acceptOrRejectAllChanges = useCallback(
    (variant: TrackedChangesAction): void => {
      const allChanges = Queries.sortedEditRanges(editor);
      if (!allChanges.length || !editingMode || !currentUserId) return;

      switch (variant) {
        case 'accept': {
          const acceptedSources = AuthorizedTransforms.acceptAllSuggestedEdits(editor, {
            authorId: currentUserId,
            editEntries: allChanges,
            editingMode,
          });
          const acceptedSourcesToUpdate = acceptedSources.filter(source => {
            return !Queries.docHasSuggestionWithGroupId(editor, { groupId: source.groupId });
          });
          if (acceptedSourcesToUpdate.length) {
            // Intentionally do not await promise to avoid blocking UI...
            syncSuggestionStatusToServer(acceptedSourcesToUpdate, SuggestionStatus.Accepted);
          }
          break;
        }
        case 'reject': {
          const rejectedSources = AuthorizedTransforms.rejectAllSuggestedEdits(editor, {
            authorId: currentUserId,
            editEntries: allChanges,
            editingMode,
          });
          const rejectedSourcesToUpdate = rejectedSources.filter(source => {
            return !Queries.docHasSuggestionWithGroupId(editor, { groupId: source.groupId });
          });
          if (rejectedSourcesToUpdate.length) {
            // Intentionally do not await promise to avoid blocking UI...
            syncSuggestionStatusToServer(rejectedSourcesToUpdate, SuggestionStatus.Rejected);
          }
          break;
        }
      }

      setSuggestedEdits([]);
    },
    [
      AuthorizedTransforms,
      currentUserId,
      editingMode,
      editor,
      setSuggestedEdits,
      syncSuggestionStatusToServer,
    ]
  );

  return {
    acceptOrRejectAllChanges,
    acceptOrRejectSelectedChanges,
    acceptOrRejectSelectedChangesAndMoveToNext,
    keepSelectedChanges,
  };
}

/** Get the suggested edits the selection covers, the same ones `acceptOrRejectSelectedChanges` resolves. */
export function useGetSelectedSuggestedEdits(): {
  selectedSuggestedEdits: EditEntries;
} {
  const editor = useEditor();
  const selection = useSelection();
  const suggestedEdits = useEditorTabStateSelector(state => state.derived.suggestedEdits, equals);

  if (!selection) return { selectedSuggestedEdits: [] };

  return { selectedSuggestedEdits: Queries.selectedEdits(editor, suggestedEdits) };
}
